import { useRef } from 'react';
import { useAppel } from '../../appels/useAppel';
import { usePiegeFocus } from '../../lib/usePiegeFocus';

/**
 * Appel en cours de sonnerie côté appelant — sans cet écran, cliquer sur
 * « Appeler » ne donnait aucun retour visuel tant que l'autre n'avait pas
 * répondu, et aucun moyen d'annuler avant décroché.
 */
export function AppelSortant(): JSX.Element | null {
  const { etat, raccrocher } = useAppel();
  // Au clavier (TF-341) : focus dans la fenêtre, `Échap` annule l'appel.
  const fenetre = useRef<HTMLDivElement>(null);
  usePiegeFocus(fenetre, etat.phase === 'appel-sortant', raccrocher, { focusInitial: 'premier' });

  if (etat.phase !== 'appel-sortant') return null;

  const { contact, typeAppel } = etat;
  const nomContact = `${contact.prenom} ${contact.nom}`.trim() || 'Contact';

  return (
    <div
      ref={fenetre}
      role="dialog"
      aria-modal="true"
      aria-label={`Appel ${typeAppel === 'video' ? 'visio' : 'audio'} vers ${nomContact}`}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-codialis-navy/60 p-4"
    >
      <div className="w-full max-w-sm rounded-3xl bg-white p-4 sm:p-8 text-center shadow-card">
        <div className="mx-auto flex h-24 w-24 items-center justify-center rounded-full bg-codialis-navy font-display text-3xl font-bold text-white">
          {contact.initiales}
        </div>
        <div className="mt-4 font-display text-xl font-bold text-codialis-navy">{nomContact}</div>
        <div className="mt-1 flex items-center justify-center gap-1.5 text-sm text-codialis-gray">
          <span className="flex h-2 w-2 animate-ping rounded-full bg-codialis-sky" aria-hidden="true" />
          Appel {typeAppel === 'video' ? 'visio' : 'audio'}… ça sonne
        </div>

        <button
          type="button"
          onClick={raccrocher}
          aria-label="Annuler l'appel"
          className="mx-auto mt-8 flex h-16 w-16 items-center justify-center rounded-full bg-red-600 text-white shadow-lg transition hover:bg-red-700 active:scale-95"
        >
          <svg aria-hidden="true" className="h-7 w-7 rotate-[135deg]" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
            />
          </svg>
        </button>
      </div>
    </div>
  );
}
